import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Category, Product, ProductFacets } from '@rosita/shared';
import { api } from '../../lib/api';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { Accordion } from '../../components/ui/Accordion';
import { Button } from '../../components/ui/Button';
import { ProductCard } from '../../components/ui/ProductCard';
import { Icon } from '../../components/ui/Icon';
import { sortSizes } from '../../lib/product';
import styles from './ProductsPage.module.scss';

interface ProductListResponse {
  items: Product[];
  total: number;
  totalPages: number;
  page: number;
}

function filterCategoryTree(
  tree: Category[],
  visibleSlugs: Set<string>,
): Category[] {
  const result: Category[] = [];
  for (const cat of tree) {
    const visibleChildren = (cat.children ?? []).filter((c) =>
      visibleSlugs.has(c.slug),
    );
    if (visibleSlugs.has(cat.slug) || visibleChildren.length > 0) {
      result.push({ ...cat, children: visibleChildren });
    }
  }
  return result;
}

export default function ProductsPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [facets, setFacets] = useState<ProductFacets>({
    sizes: [],
    colours: [],
    categorySlugs: [],
    materials: [],
  });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [priceMinDraft, setPriceMinDraft] = useState(sp.get('preco_min') ?? '');
  const [priceMaxDraft, setPriceMaxDraft] = useState(sp.get('preco_max') ?? '');
  const priceTimer = useRef<number | undefined>(undefined);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    const cat = sp.get('categoria');
    const size = sp.get('tamanho');
    const colour = sp.get('cor');
    const material = sp.get('material');
    const minPrice = sp.get('preco_min');
    const maxPrice = sp.get('preco_max');
    const search = sp.get('q');
    const sort = sp.get('sort');
    const page = sp.get('page') ?? '1';
    if (cat) p.set('category', cat);
    if (size) p.set('size', size);
    if (colour) p.set('colour', colour);
    if (material) p.set('material', material);
    if (minPrice) p.set('minPrice', minPrice);
    if (maxPrice) p.set('maxPrice', maxPrice);
    if (search) p.set('search', search);
    if (sort) p.set('sort', sort);
    p.set('page', page);
    p.set('limit', '24');
    return p.toString();
  }, [sp]);

  const facetParams = useMemo(() => {
    const p = new URLSearchParams();
    const cat = sp.get('categoria');
    const size = sp.get('tamanho');
    const colour = sp.get('cor');
    const material = sp.get('material');
    const minPrice = sp.get('preco_min');
    const maxPrice = sp.get('preco_max');
    const search = sp.get('q');
    if (cat) p.set('category', cat);
    if (size) p.set('size', size);
    if (colour) p.set('colour', colour);
    if (material) p.set('material', material);
    if (minPrice) p.set('minPrice', minPrice);
    if (maxPrice) p.set('maxPrice', maxPrice);
    if (search) p.set('search', search);
    return p.toString();
  }, [sp]);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      try {
        const [prodsRes, catsRes, facetsRes] = await Promise.all([
          api.get<ProductListResponse>(`/products?${params}`),
          api.get<Category[]>('/categories'),
          api.get<ProductFacets>(`/products/facets?${facetParams}`),
        ]);
        setProducts(prodsRes.data.items);
        setTotal(prodsRes.data.total);
        setTotalPages(prodsRes.data.totalPages);
        setCategories(catsRes.data);
        setFacets(facetsRes.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [params, facetParams]);

  const setFilter = (key: string, value: string | null, replace = false): void => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSp(next, { replace });
  };

  const schedulePriceWrite = (key: 'preco_min' | 'preco_max', value: string): void => {
    window.clearTimeout(priceTimer.current);
    priceTimer.current = window.setTimeout(() => {
      setFilter(key, value || null, true);
    }, 400);
  };

  const clearAll = (): void => {
    setPriceMinDraft('');
    setPriceMaxDraft('');
    setSp(new URLSearchParams());
  };

  const currentPage = Number(sp.get('page') ?? '1');
  const activeCategory = sp.get('categoria');
  const activeSize = sp.get('tamanho');
  const activeColour = sp.get('cor');
  const activeMaterial = sp.get('material');

  const visibleMaterials = useMemo(
    () => [...facets.materials].sort((a, b) => a.name.localeCompare(b.name, 'pt')),
    [facets.materials],
  );

  const visibleCategories = useMemo(
    () => filterCategoryTree(categories, new Set(facets.categorySlugs)),
    [categories, facets.categorySlugs],
  );
  const visibleSizes = useMemo(() => sortSizes(facets.sizes), [facets.sizes]);
  const visibleColours = useMemo(
    () => [...facets.colours].sort((a, b) => a.value.localeCompare(b.value, 'pt')),
    [facets.colours],
  );

  const applyMobile = (): void => {
    setDrawerOpen(false);
    document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const FilterGroups = (
    <div className={styles.filterGroups}>
      {visibleCategories.length > 0 ? (
        <Accordion title={t.products.filterGroupProducts} defaultOpen>
          <ul className={styles.optionList}>
            <li>
              <button
                type="button"
                className={`${styles.optionBtn} ${!activeCategory ? styles.optionActive : ''}`}
                onClick={() => setFilter('categoria', null)}
              >
                {t.products.all}
              </button>
            </li>
            {visibleCategories.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`${styles.optionBtn} ${activeCategory === c.slug ? styles.optionActive : ''}`}
                  onClick={() => setFilter('categoria', c.slug)}
                >
                  {c.name}
                </button>
                {c.children && c.children.length > 0 ? (
                  <ul className={styles.optionList}>
                    {c.children.map((sub) => (
                      <li key={sub.id}>
                        <button
                          type="button"
                          className={`${styles.optionBtn} ${styles.optionSub} ${activeCategory === sub.slug ? styles.optionActive : ''}`}
                          onClick={() => setFilter('categoria', sub.slug)}
                        >
                          {sub.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </Accordion>
      ) : null}

      {visibleSizes.length > 0 ? (
        <Accordion title={t.products.filterGroupSize}>
          <div className={styles.sizeRow}>
            {visibleSizes.map((s) => (
              <button
                key={s}
                type="button"
                className={`${styles.chip} ${activeSize === s ? styles.chipActive : ''}`}
                onClick={() => setFilter('tamanho', activeSize === s ? null : s)}
              >
                {s}
              </button>
            ))}
          </div>
        </Accordion>
      ) : null}

      {visibleColours.length > 0 ? (
        <Accordion title={t.products.filterGroupColour}>
          <ul className={styles.colourList}>
            {visibleColours.map((c) => (
              <li key={c.value}>
                <button
                  type="button"
                  className={`${styles.colourBtn} ${activeColour === c.value ? styles.colourActive : ''}`}
                  onClick={() =>
                    setFilter('cor', activeColour === c.value ? null : c.value)
                  }
                >
                  <span
                    className={styles.swatch}
                    style={{ background: c.hex ?? 'var(--color-surface-alt)' }}
                    aria-hidden
                  />
                  <span>{c.value}</span>
                </button>
              </li>
            ))}
          </ul>
        </Accordion>
      ) : null}

      {visibleMaterials.length > 0 ? (
        <Accordion title={t.products.filterGroupMaterial}>
          <ul className={styles.optionList}>
            <li>
              <button
                type="button"
                className={`${styles.optionBtn} ${!activeMaterial ? styles.optionActive : ''}`}
                onClick={() => setFilter('material', null)}
              >
                {t.products.allMaterials}
              </button>
            </li>
            {visibleMaterials.map((m) => (
              <li key={m.slug}>
                <button
                  type="button"
                  className={`${styles.optionBtn} ${activeMaterial === m.slug ? styles.optionActive : ''}`}
                  onClick={() =>
                    setFilter('material', activeMaterial === m.slug ? null : m.slug)
                  }
                >
                  {m.name}
                </button>
              </li>
            ))}
          </ul>
        </Accordion>
      ) : null}

      <Accordion title={t.products.filterGroupPrice}>
        <div className={styles.priceRow}>
          <input
            type="number"
            inputMode="numeric"
            placeholder={t.products.minPrice}
            value={priceMinDraft}
            onChange={(e) => {
              setPriceMinDraft(e.target.value);
              schedulePriceWrite('preco_min', e.target.value);
            }}
            className={styles.priceInput}
            aria-label={`${t.products.filterGroupPrice} ${t.products.minPrice}`}
          />
          <span aria-hidden>—</span>
          <input
            type="number"
            inputMode="numeric"
            placeholder={t.products.maxPrice}
            value={priceMaxDraft}
            onChange={(e) => {
              setPriceMaxDraft(e.target.value);
              schedulePriceWrite('preco_max', e.target.value);
            }}
            className={styles.priceInput}
            aria-label={`${t.products.filterGroupPrice} ${t.products.maxPrice}`}
          />
        </div>
      </Accordion>
    </div>
  );

  return (
    <>
      <SEO title={t.products.title} description="Veja todos os produtos da Rosita Store" />
      <div className="container">
        <nav className={styles.breadcrumb} aria-label={t.common.breadcrumb}>
          <Link to="/">{t.products.breadcrumbHome}</Link>
          <span aria-hidden> / </span>
          <span>{t.products.breadcrumbShop}</span>
        </nav>

        <header className={styles.pageHeader}>
          <h1 className={`h1-display ${styles.title}`}>{t.products.title}</h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.filtersToggle}
              onClick={() => setDrawerOpen(true)}
              aria-label={t.products.openFilters}
            >
              <Icon name="menu" size={18} />
              <span>{t.products.openFilters}</span>
            </button>
            <label className={styles.sortLabel}>
              <span className="sr-only">{t.products.sortBy}</span>
              <select
                value={sp.get('sort') ?? 'newest'}
                onChange={(e) => setFilter('sort', e.target.value)}
                className={styles.sortSelect}
              >
                <option value="newest">Mais recentes</option>
                <option value="price_asc">Preço: menor primeiro</option>
                <option value="price_desc">Preço: maior primeiro</option>
                <option value="name_asc">Nome: A-Z</option>
              </select>
              <Icon name="chevron-down" size={14} className={styles.sortChev} />
            </label>
          </div>
        </header>

        <div className={styles.layout}>
          <aside className={styles.filters} aria-label="Filtros">
            <button
              type="button"
              className={styles.clearLink}
              onClick={clearAll}
            >
              {t.products.clearFilters}
            </button>
            {FilterGroups}
          </aside>

          <div id="results" className={styles.results}>
            <p className={styles.resultCount}>
              {total} {t.products.resultsCount}
            </p>
            {loading ? (
              <p className={styles.muted}>{t.common.loading}</p>
            ) : products.length === 0 ? (
              <p className={styles.muted}>{t.products.noResults}</p>
            ) : (
              <div className={styles.grid}>
                {products.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <nav className={styles.pagination} aria-label="Paginação">
                <button
                  type="button"
                  onClick={() => setFilter('page', String(currentPage - 1))}
                  disabled={currentPage <= 1}
                  className={styles.pageBtn}
                >
                  <Icon name="arrow-left" size={16} />
                  <span>Anterior</span>
                </button>
                <span className={styles.pageInfo}>
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setFilter('page', String(currentPage + 1))}
                  disabled={currentPage >= totalPages}
                  className={styles.pageBtn}
                >
                  <span>Seguinte</span>
                  <Icon name="arrow-right" size={16} />
                </button>
              </nav>
            ) : null}
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {drawerOpen ? (
        <div
          className={styles.drawer}
          role="dialog"
          aria-modal="true"
          aria-label="Filtros"
        >
          <div className={styles.drawerHeader}>
            <h2 className={styles.drawerTitle}>{t.products.openFilters}</h2>
            <button
              type="button"
              className={styles.drawerClose}
              onClick={() => setDrawerOpen(false)}
              aria-label={t.products.closeFilters}
            >
              <Icon name="close" size={22} />
            </button>
          </div>
          <div className={styles.drawerBody}>
            <button type="button" className={styles.clearLink} onClick={clearAll}>
              {t.products.clearFilters}
            </button>
            {FilterGroups}
          </div>
          <div className={styles.drawerFooter}>
            <Button variant="primary" size="lg" fullWidth onClick={applyMobile}>
              {t.products.applyFilters}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
